import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../api'
import { useUserStore } from './userStore'
import type { ChatUnreadDto } from '../dtos'

export const useChatNotificationStore = defineStore('chatNotification', () => {
    const userStore = useUserStore()
    const unreads = ref<ChatUnreadDto[]>([])

    let pollTimer: number | null = null

    const totalUnread = computed(() => {
        let count = 0
        for (const band of unreads.value) {
            count += band.unreadCount
            if (band.songs && Array.isArray(band.songs)) {
                for (const song of band.songs) {
                    count += song.unreadCount
                }
            }
        }
        return count
    })

    async function fetchUnreads() {
        if (!userStore.user) return

        try {
            const token = await userStore.user.getIdToken()
            const response = await api.get<ChatUnreadDto[]>('/chat/unread', {
                headers: { Authorization: `Bearer ${token}` }
            })
            
            // Defensively handle response data
            if (response.data && Array.isArray(response.data)) {
                unreads.value = response.data.map(band => ({
                    ...band,
                    songs: Array.isArray(band.songs) ? band.songs : []
                }))
            } else {
                unreads.value = []
            }
        } catch (error) {
            console.error('Failed to fetch unreads:', error)
            unreads.value = []
        }
    }

    async function markBandChatAsRead(bandId: number) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            await api.post(`/chat/read/band/${bandId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            })
            // Remove band unread from local state
            const band = unreads.value.find(u => u.bandId === bandId)
            if (band) band.unreadCount = 0
            // Clean up if no song unreads either
            unreads.value = unreads.value.filter(u => u.unreadCount > 0 || u.songs.length > 0)
        } catch {
            // Silent fail
        }
    }

    async function markSongChatAsRead(bandId: number, songId: number) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            await api.post(`/chat/read/song/${songId}?bandId=${bandId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            })
            // Remove song unread from local state
            const band = unreads.value.find(u => u.bandId === bandId)
            if (band) {
                band.songs = band.songs.filter(s => s.songId !== songId)
            }
            unreads.value = unreads.value.filter(u => u.unreadCount > 0 || u.songs.length > 0)
        } catch {
            // Silent fail
        }
    }

    async function markAllAsRead() {
        if (!userStore.user) return
        const token = await userStore.user.getIdToken()
        const promises: Promise<unknown>[] = []
        for (const band of unreads.value) {
            if (band.unreadCount > 0) {
                promises.push(api.post(`/chat/read/band/${band.bandId}`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                }))
            }
            for (const song of band.songs || []) {
                promises.push(api.post(`/chat/read/song/${song.songId}?bandId=${band.bandId}`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                }))
            }
        }
        try {
            await Promise.all(promises)
            unreads.value = []
        } catch {
            // Silent fail
        }
    }

    function startPolling() {
        stopPolling()
        fetchUnreads()
        pollTimer = window.setInterval(() => fetchUnreads(), 10000)
    }

    function stopPolling() {
        if (pollTimer) {
            clearInterval(pollTimer)
            pollTimer = null
        }
    }

    function clear() {
        stopPolling()
        unreads.value = []
    }

    return {
        unreads, totalUnread,
        fetchUnreads, markBandChatAsRead, markSongChatAsRead, markAllAsRead,
        startPolling, stopPolling, clear
    }
})
